import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check } from 'lucide-react';
import { LANGUAGES } from '../i18n/config';

export default function LanguageSwitcher({ variant = 'header' }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const current = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const change = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  const isMobile = variant === 'mobile';

  return (
    <div ref={ref} className="relative" data-testid="language-switcher">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="language-switcher-toggle"
        className={
          isMobile
            ? "flex items-center gap-2 text-lg font-serif text-[#0B1F3F]"
            : "flex items-center gap-2 text-[11px] font-sans tracking-[0.2em] uppercase text-[#6B5B4A] hover:text-[#0B1F3F] transition-colors py-2 px-3 border border-transparent hover:border-[#0B1F3F]/20"
        }
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Globe size={isMobile ? 18 : 13} />
        <span>{current.label}</span>
      </button>

      {open && (
        <ul
          role="listbox"
          data-testid="language-switcher-menu"
          className={
            isMobile
              ? "mt-3 flex flex-col gap-2 border-t border-[#0B1F3F]/10 pt-3"
              : "absolute right-0 top-full mt-2 min-w-[180px] bg-[#F5EFE1] border border-[#0B1F3F]/20 shadow-2xl z-[60] py-2"
          }
        >
          {LANGUAGES.map((lang) => {
            const active = lang.code === i18n.language;
            return (
              <li key={lang.code}>
                <button
                  type="button"
                  onClick={() => change(lang.code)}
                  data-testid={`language-option-${lang.code}`}
                  className={
                    isMobile
                      ? `flex items-center justify-between w-full text-left font-sans text-sm py-2 ${active ? 'text-[#B58F45]' : 'text-[#0B1F3F]'}`
                      : `flex items-center justify-between w-full text-left px-4 py-2 font-sans text-[12px] tracking-wider transition-colors ${active ? 'text-[#B58F45]' : 'text-[#0B1F3F] hover:bg-[#0B1F3F]/5'}`
                  }
                >
                  <span>
                    <span className="tracking-[0.2em] mr-3">{lang.label}</span>
                    <span className={isMobile ? "text-[#6B5B4A]" : "text-[#6B5B4A] text-[11px]"}>{lang.name}</span>
                  </span>
                  {active && <Check size={12} />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
